import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Anchor, LogOut, RefreshCw, Mail, Phone, CalendarDays, Lock, Trash2, Plus, Save, Download, MailCheck, CheckCircle2, Hourglass, ArrowUpRight, BellRing, BarChart3 } from "lucide-react";
import { PERCORSO_OPTIONS, DIAGNOSI_OPTIONS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const percorsoLabel = (v) => PERCORSO_OPTIONS.find((o) => o.value === v)?.label || v;
const diagnosiLabel = (v) => DIAGNOSI_OPTIONS.find((o) => o.value === v)?.label || v;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-CH", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const calInputCls =
  "w-full rounded-lg border border-[#E2D9CD] bg-[#FAF8F5] px-3 py-2 text-sm text-[#0C1B33] outline-none transition-colors focus:border-[#0D9488]";

const AdminPage = () => {
  const [access, setAccess] = useState(localStorage.getItem("arc_access") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [cal, setCal] = useState(null);
  const [posti, setPosti] = useState(null);
  const [savingCal, setSavingCal] = useState(false);
  const [sendingRem, setSendingRem] = useState("");
  const [highlightId, setHighlightId] = useState("");

  const logout = useCallback(() => {
    localStorage.removeItem("arc_access");
    localStorage.removeItem("arc_refresh");
    setAccess("");
    setItems(null);
    setCal(null);
  }, []);

  const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

  const load = useCallback(
    async (token) => {
      try {
        const { data } = await axios.get(`${API}/admin/iscrizioni`, { headers: authHeaders(token) });
        setItems(data);
        const calRes = await axios.get(`${API}/calendario`);
        setCal(calRes.data.items || []);
        const postiRes = await axios.get(`${API}/posti`);
        setPosti(postiRes.data);
      } catch (e) {
        if (e.response?.status === 401) {
          const refresh = localStorage.getItem("arc_refresh");
          if (refresh) {
            try {
              const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
              localStorage.setItem("arc_access", data.access_token);
              setAccess(data.access_token);
              return load(data.access_token);
            } catch {
              logout();
              return;
            }
          }
          logout();
        } else {
          setError("Impossibile caricare i dati.");
        }
      }
    },
    [logout]
  );

  useEffect(() => {
    if (access) load(access);
  }, [access, load]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("arc_access", data.access_token);
      localStorage.setItem("arc_refresh", data.refresh_token);
      setAccess(data.access_token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Accesso non riuscito. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    try {
      const res = await axios.get(`${API}/admin/iscrizioni/export`, {
        headers: authHeaders(access),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "iscrizioni-arcipelago.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("File Excel scaricato");
    } catch {
      toast.error("Download non riuscito");
    }
  };

  const confermaIscrizione = async (it) => {
    if (!window.confirm(`Inviare l'email di conferma a ${it.nome} ${it.cognome} (${it.email})?`)) return;
    try {
      await axios.post(`${API}/admin/iscrizioni/${it.id}/conferma`, {}, { headers: authHeaders(access) });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, confermata: true } : x)));
      toast.success(`Email di conferma inviata a ${it.email}`);
    } catch {
      toast.error("Invio della conferma non riuscito");
    }
  };

  const promuovi = async (it) => {
    try {
      await axios.post(`${API}/admin/iscrizioni/${it.id}/promuovi`, {}, { headers: authHeaders(access) });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, lista_attesa: false } : x)));
      const postiRes = await axios.get(`${API}/posti`);
      setPosti(postiRes.data);
      if (highlightId === it.id) setHighlightId("");
      toast.success(`${it.nome} ${it.cognome} spostato/a nei posti effettivi`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Promozione non riuscita");
    }
  };

  const inviaPromemoria = async (row) => {
    if (!window.confirm(`Inviare il promemoria per "${row.titolo}" (${row.data}) a tutti gli iscritti del gruppo in presenza?`)) return;
    setSendingRem(row.id);
    try {
      const { data } = await axios.post(`${API}/admin/promemoria/${row.id}`, {}, { headers: authHeaders(access) });
      toast.success(`Promemoria inviato a ${data.inviati} iscritti su ${data.totale}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio del promemoria non riuscito");
    } finally {
      setSendingRem("");
    }
  };

  const toggleEsaurito = async () => {
    try {
      await axios.put(
        `${API}/admin/impostazioni`,
        { presenza_esaurita: !posti?.presenza_esaurita },
        { headers: authHeaders(access) }
      );
      const postiRes = await axios.get(`${API}/posti`);
      setPosti(postiRes.data);
      toast.success("Disponibilità 2026 aggiornata sul sito");
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const deleteIscrizione = async (it) => {
    if (!window.confirm(`Eliminare l'iscrizione di ${it.nome} ${it.cognome}?`)) return;
    try {
      await axios.delete(`${API}/admin/iscrizioni/${it.id}`, { headers: authHeaders(access) });
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      const postiRes = await axios.get(`${API}/posti`);
      setPosti(postiRes.data);
      if (highlightId === it.id) setHighlightId("");
      const prossimo = items
        .filter((x) => x.lista_attesa && x.scelta_percorso === it.scelta_percorso && x.id !== it.id)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0];
      if (prossimo && !it.lista_attesa) {
        setHighlightId(prossimo.id);
        toast.success("Iscrizione eliminata — si è liberato un posto!", {
          description: `Promuovi per primo/a: ${prossimo.nome} ${prossimo.cognome} (evidenziato/a sotto).`,
          duration: 8000,
        });
      } else {
        toast.success("Iscrizione eliminata");
      }
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const updateCalRow = (idx, key, value) =>
    setCal((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));

  const addCalRow = () =>
    setCal((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        anno: 2027,
        numero: (prev.filter((r) => r.anno === 2027).length || 0) + 1,
        titolo: "",
        data: "",
        ora: "17:00 – 18:30",
        modalita: "presenza",
      },
    ]);

  const removeCalRow = (idx) => setCal((prev) => prev.filter((_, i) => i !== idx));

  const saveCal = async () => {
    const incomplete = cal.some((r) => !r.titolo.trim() || !r.data.trim() || !r.ora.trim());
    if (incomplete) {
      toast.error("Completa titolo, data e ora in tutte le righe prima di salvare.");
      return;
    }
    setSavingCal(true);
    try {
      const payload = {
        items: cal.map((r) => ({
          anno: Number(r.anno),
          numero: Number(r.numero),
          titolo: r.titolo.trim(),
          data: r.data.trim(),
          ora: r.ora.trim(),
          modalita: r.modalita || "presenza",
        })),
      };
      await axios.put(`${API}/admin/calendario`, payload, { headers: authHeaders(access) });
      toast.success("Calendario aggiornato", { description: "Le nuove date sono già visibili sul sito." });
      load(access);
    } catch (e) {
      toast.error(e.response?.status === 401 ? "Sessione scaduta: effettua di nuovo l'accesso." : "Salvataggio non riuscito");
      if (e.response?.status === 401) logout();
    } finally {
      setSavingCal(false);
    }
  };

  if (!access) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0C1B33] px-4" data-testid="admin-login-page">
        <form
          onSubmit={submitLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-2xl border border-[#FAF8F5]/12 bg-[#FAF8F5]/[0.05] p-8 backdrop-blur"
        >
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#14B8A6]/15 text-[#14B8A6]">
              <Lock className="h-6 w-6" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-[#FAF8F5]">Area riservata</h1>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-[#FAF8F5]/50">
              Arcipelago · Archivio iscrizioni
            </p>
          </div>

          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-[#FAF8F5]/60" htmlFor="admin-email">
            Email
          </label>
          <input
            id="admin-email"
            data-testid="admin-login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mb-4 w-full rounded-xl border border-[#FAF8F5]/15 bg-[#0C1B33] px-4 py-3 text-sm text-[#FAF8F5] outline-none transition-colors focus:border-[#14B8A6]"
          />
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-[#FAF8F5]/60" htmlFor="admin-password">
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mb-5 w-full rounded-xl border border-[#FAF8F5]/15 bg-[#0C1B33] px-4 py-3 text-sm text-[#FAF8F5] outline-none transition-colors focus:border-[#14B8A6]"
          />

          {error && (
            <p data-testid="admin-login-error" className="mb-4 rounded-lg bg-[#C93B2B]/15 px-3 py-2 text-xs text-[#F3A39A]">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full rounded-full bg-[#0D9488] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#14B8A6] disabled:opacity-60"
          >
            {loading ? "Accesso in corso…" : "Accedi"}
          </button>

          <a href="/" data-testid="admin-back-home" className="mt-5 block text-center text-xs text-[#FAF8F5]/50 transition-colors hover:text-[#14B8A6]">
            ← Torna al sito
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4EFEA]" data-testid="admin-dashboard">
      <header className="border-b border-[#E2D9CD] bg-[#0C1B33]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-8">
          <div className="flex items-center gap-2 text-[#FAF8F5]">
            <Anchor className="h-5 w-5 text-[#14B8A6]" />
            <span className="font-display text-xl font-semibold">Arcipelago</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#14B8A6]">
              Area riservata
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load(access)}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#FAF8F5]/25 px-4 py-2 text-xs font-semibold text-[#FAF8F5] transition-colors hover:border-[#14B8A6] hover:text-[#14B8A6]"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Aggiorna
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#C93B2B] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#a52f22]"
            >
              <LogOut className="h-3.5 w-3.5" />
              Esci
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-12 px-4 py-10 sm:px-8">
        {items !== null && (
          <section data-testid="admin-stats-section">
            <h2 className="mb-5 flex items-center gap-2 font-display text-2xl font-semibold text-[#0C1B33]">
              <BarChart3 className="h-6 w-6 text-[#0D9488]" />
              Riepilogo
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-[#E2D9CD] bg-[#FFFDF9] p-6" data-testid="admin-stats-percorso">
                <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.14em] text-[#718096]">
                  Iscritti per percorso (posti effettivi)
                </p>
                {(() => {
                  const effettivi = items.filter((x) => !x.lista_attesa);
                  const rows = PERCORSO_OPTIONS.map((o) => ({
                    label: o.label,
                    n: effettivi.filter((x) => x.scelta_percorso === o.value).length,
                  }));
                  const max = Math.max(1, ...rows.map((r) => r.n));
                  const colors = ["bg-[#0C1B33]", "bg-[#0D9488]", "bg-amber-500"];
                  return rows.map((r, idx) => (
                    <div key={r.label} className="mb-3 last:mb-0">
                      <div className="mb-1 flex justify-between text-xs font-medium text-[#334155]">
                        <span>{r.label}</span>
                        <strong>{r.n}</strong>
                      </div>
                      <div className="h-2.5 overflow-hidden rounded-full bg-[#F4EFEA]">
                        <div
                          className={`h-full rounded-full ${colors[idx]} transition-[width] duration-700`}
                          style={{ width: `${(r.n / max) * 100}%` }}
                        />
                      </div>
                    </div>
                  ));
                })()}
              </div>
              <div className="rounded-2xl border border-[#E2D9CD] bg-[#FFFDF9] p-6" data-testid="admin-stats-mese">
                <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.14em] text-[#718096]">
                  Richieste per mese
                </p>
                {(() => {
                  const perMese = {};
                  items.forEach((x) => {
                    const d = new Date(x.created_at);
                    if (isNaN(d)) return;
                    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                    perMese[k] = (perMese[k] || 0) + 1;
                  });
                  const mesi = Object.entries(perMese).sort((a, b) => a[0].localeCompare(b[0]));
                  if (mesi.length === 0) return <p className="text-sm text-[#718096]">Nessun dato ancora.</p>;
                  const max = Math.max(1, ...mesi.map((m) => m[1]));
                  return mesi.map(([k, n]) => (
                    <div key={k} className="mb-3 last:mb-0">
                      <div className="mb-1 flex justify-between text-xs font-medium capitalize text-[#334155]">
                        <span>{new Date(`${k}-01T12:00:00`).toLocaleDateString("it-CH", { month: "long", year: "numeric" })}</span>
                        <strong>{n}</strong>
                      </div>
                      <div className="h-2.5 overflow-hidden rounded-full bg-[#F4EFEA]">
                        <div className="h-full rounded-full bg-[#0D9488] transition-[width] duration-700" style={{ width: `${(n / max) * 100}%` }} />
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </section>
        )}

        <section data-testid="admin-iscrizioni-section">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-2xl font-semibold text-[#0C1B33]">Iscrizioni ricevute</h2>
            <button
              data-testid="admin-export-excel"
              onClick={exportExcel}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#0C1B33]/25 bg-[#FFFDF9] px-4 py-2 text-xs font-semibold text-[#0C1B33] transition-colors hover:border-[#0D9488] hover:text-[#0D9488]"
            >
              <Download className="h-3.5 w-3.5" />
              Scarica Excel
            </button>
          </div>
          {posti && (
            <div className="mb-6 flex flex-wrap gap-3" data-testid="admin-posti-chips">
              <span className="rounded-full bg-[#0C1B33] px-4 py-1.5 text-xs font-semibold text-white" data-testid="admin-posti-presenza">
                Presenza ciclo 1: {posti.cicli.presenza_1.iscritti}/8 · {posti.cicli.presenza_1.rimasti} liberi
              </span>
              <span className="rounded-full bg-[#0C1B33] px-4 py-1.5 text-xs font-semibold text-white" data-testid="admin-posti-ciclo2">
                Presenza ciclo 2: {posti.cicli.presenza_2.iscritti}/8 · {posti.cicli.presenza_2.rimasti} liberi
              </span>
              <span className="rounded-full bg-[#0D9488] px-4 py-1.5 text-xs font-semibold text-white" data-testid="admin-posti-online">
                Online ciclo 1: {posti.cicli.online_1.iscritti}/{posti.cicli.online_1.capienza} · {posti.cicli.online_1.rimasti} liberi
              </span>
              <span className="rounded-full bg-[#0D9488] px-4 py-1.5 text-xs font-semibold text-white" data-testid="admin-posti-online2">
                Online ciclo 2: {posti.cicli.online_2.iscritti}/{posti.cicli.online_2.capienza} · {posti.cicli.online_2.rimasti} liberi
              </span>
              <label
                data-testid="admin-toggle-esaurito"
                className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                  posti.presenza_esaurita
                    ? "border-[#C93B2B]/40 bg-[#C93B2B]/10 text-[#C93B2B]"
                    : "border-[#0C1B33]/25 bg-[#FFFDF9] text-[#0C1B33] hover:border-[#C93B2B]/50"
                }`}
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-[#C93B2B]"
                  checked={Boolean(posti.presenza_esaurita)}
                  onChange={toggleEsaurito}
                />
                Posti 2026 esauriti (gruppo in presenza)
              </label>
            </div>
          )}
          {items === null ? (
            <p className="py-16 text-center text-sm text-[#718096]" data-testid="admin-loading">Caricamento…</p>
          ) : items.length === 0 ? (
            <p className="py-16 text-center text-sm text-[#718096]" data-testid="admin-empty">
              Nessuna iscrizione ricevuta finora.
            </p>
          ) : (
            <>
              <p className="mb-6 text-sm text-[#475569]" data-testid="admin-count">
                <strong className="text-[#0C1B33]">{items.filter((x) => !x.lista_attesa).length}</strong> iscrizion{items.filter((x) => !x.lista_attesa).length === 1 ? "e ricevuta" : "i ricevute"}
                {items.some((x) => x.lista_attesa) && (
                  <> · <strong className="text-amber-700">{items.filter((x) => x.lista_attesa).length}</strong> in lista d'attesa (sotto)</>
                )}
              </p>
              <div className="space-y-4" data-testid="admin-iscrizioni-list">
                {items.filter((it) => !it.lista_attesa).map((it, i) => (
                  <article
                    key={it.id || i}
                    data-testid={`admin-iscrizione-${i}`}
                    className="rounded-2xl border border-[#E2D9CD] bg-[#FFFDF9] p-6 shadow-[0_6px_20px_rgba(12,27,51,0.05)]"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display text-xl font-semibold text-[#0C1B33]">
                          {it.nome} {it.cognome}
                        </h3>
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-[#718096]">
                          <CalendarDays className="h-3.5 w-3.5 text-[#0D9488]" />
                          Ricevuta il {fmtDate(it.created_at)} · Nato/a il {it.data_nascita}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#0D9488]/10 px-3.5 py-1.5 text-xs font-bold text-[#0D9488]">
                          {percorsoLabel(it.scelta_percorso)}
                        </span>
                        {it.ciclo && (
                          <span className="rounded-full bg-[#0C1B33]/10 px-3.5 py-1.5 text-xs font-bold text-[#0C1B33]">
                            {it.ciclo === "ciclo_2" ? "2° ciclo 2027" : "1° ciclo 2027"}
                          </span>
                        )}
                        {it.confermata ? (
                          <span
                            data-testid={`admin-iscrizione-confermata-${i}`}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#0D9488] px-3.5 py-1.5 text-xs font-bold text-white"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Confermata
                          </span>
                        ) : (
                          <button
                            data-testid={`admin-iscrizione-conferma-${i}`}
                            onClick={() => confermaIscrizione(it)}
                            title="Invia email di conferma all'iscritto/a"
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#0D9488]/40 px-3.5 py-1.5 text-xs font-bold text-[#0D9488] transition-colors hover:bg-[#0D9488] hover:text-white"
                          >
                            <MailCheck className="h-3.5 w-3.5" />
                            Conferma partecipazione
                          </button>
                        )}
                        <button
                          data-testid={`admin-iscrizione-delete-${i}`}
                          onClick={() => deleteIscrizione(it)}
                          title="Elimina iscrizione"
                          className="rounded-full border border-[#C93B2B]/30 p-2 text-[#C93B2B] transition-colors hover:bg-[#C93B2B] hover:text-white"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-[#334155] sm:grid-cols-2">
                      <a href={`mailto:${it.email}`} className="flex items-center gap-2 transition-colors hover:text-[#0D9488]">
                        <Mail className="h-4 w-4 text-[#0D9488]" />
                        {it.email}
                      </a>
                      <a href={`tel:${it.telefono}`} className="flex items-center gap-2 transition-colors hover:text-[#0D9488]">
                        <Phone className="h-4 w-4 text-[#0D9488]" />
                        {it.telefono}
                      </a>
                      <p className="text-xs text-[#718096] sm:col-span-2">
                        Diagnosi ADHD: <strong className="text-[#0C1B33]">{diagnosiLabel(it.diagnosi_adhd)}</strong>
                        {it.tempo_diagnosi ? <> · Da quanto tempo: <strong className="text-[#0C1B33]">{it.tempo_diagnosi}</strong></> : null}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>

        {items !== null && items.some((it) => it.lista_attesa) && (
          <section data-testid="admin-lista-attesa-section">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/15 text-amber-600">
                <Hourglass className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-display text-2xl font-semibold text-[#0C1B33]">Lista d'attesa</h2>
                <p className="text-sm text-[#718096]">
                  Richieste arrivate a posti esauriti: non occupano un posto finché non confermi tu la partecipazione.
                </p>
              </div>
            </div>
            <div className="space-y-4" data-testid="admin-lista-attesa-list">
              {items.filter((it) => it.lista_attesa).sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((it, i) => (
                <article
                  key={it.id || `attesa-${i}`}
                  data-testid={`admin-attesa-${i}`}
                  className={`rounded-2xl border p-6 transition-shadow duration-500 ${
                    it.id === highlightId
                      ? "border-amber-500 bg-amber-50 shadow-[0_0_0_4px_rgba(245,158,11,0.2),0_14px_34px_rgba(245,158,11,0.15)]"
                      : "border-amber-400/50 bg-amber-50/70"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-xl font-semibold text-[#0C1B33]">
                        {it.nome} {it.cognome}
                      </h3>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-[#718096]">
                        <CalendarDays className="h-3.5 w-3.5 text-amber-600" />
                        Richiesta il {fmtDate(it.created_at)} · Nato/a il {it.data_nascita}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-amber-500/15 px-3.5 py-1.5 text-xs font-bold text-amber-700">
                        In attesa · {percorsoLabel(it.scelta_percorso)}
                        {it.ciclo ? ` · ${it.ciclo === "ciclo_2" ? "2° ciclo" : "1° ciclo"}` : ""}
                      </span>
                      {it.id === highlightId && (
                        <span data-testid="admin-attesa-primo" className="animate-pulse rounded-full bg-[#C93B2B] px-3.5 py-1.5 text-xs font-bold text-white">
                          Promuovi per primo/a
                        </span>
                      )}
                      <button
                        data-testid={`admin-attesa-promuovi-${i}`}
                        onClick={() => promuovi(it)}
                        title="Sposta nei posti effettivi (se libero)"
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#0C1B33]/25 bg-white px-3.5 py-1.5 text-xs font-bold text-[#0C1B33] transition-colors hover:border-[#0D9488] hover:bg-[#0D9488] hover:text-white"
                      >
                        <ArrowUpRight className="h-3.5 w-3.5" />
                        Promuovi ai posti effettivi
                      </button>
                      {it.confermata ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0D9488] px-3.5 py-1.5 text-xs font-bold text-white">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Confermata
                        </span>
                      ) : (
                        <button
                          data-testid={`admin-attesa-conferma-${i}`}
                          onClick={() => confermaIscrizione(it)}
                          title="Invia email di conferma all'iscritto/a"
                          className="inline-flex items-center gap-1.5 rounded-full border border-[#0D9488]/40 bg-white px-3.5 py-1.5 text-xs font-bold text-[#0D9488] transition-colors hover:bg-[#0D9488] hover:text-white"
                        >
                          <MailCheck className="h-3.5 w-3.5" />
                          Conferma partecipazione
                        </button>
                      )}
                      <button
                        data-testid={`admin-attesa-delete-${i}`}
                        onClick={() => deleteIscrizione(it)}
                        title="Elimina dalla lista d'attesa"
                        className="rounded-full border border-[#C93B2B]/30 bg-white p-2 text-[#C93B2B] transition-colors hover:bg-[#C93B2B] hover:text-white"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-[#334155] sm:grid-cols-2">
                    <a href={`mailto:${it.email}`} className="flex items-center gap-2 transition-colors hover:text-[#0D9488]">
                      <Mail className="h-4 w-4 text-[#0D9488]" />
                      {it.email}
                    </a>
                    <a href={`tel:${it.telefono}`} className="flex items-center gap-2 transition-colors hover:text-[#0D9488]">
                      <Phone className="h-4 w-4 text-[#0D9488]" />
                      {it.telefono}
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <section data-testid="admin-promemoria-section">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0D9488]/15 text-[#0D9488]">
              <BellRing className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-2xl font-semibold text-[#0C1B33]">Promemoria incontri</h2>
              <p className="text-sm text-[#718096]">
                Due giorni prima di ogni incontro, invia il promemoria a tutti gli iscritti del gruppo in presenza.
              </p>
            </div>
          </div>
          {cal === null ? (
            <p className="py-6 text-sm text-[#718096]">Caricamento…</p>
          ) : (
            <div className="space-y-3" data-testid="admin-promemoria-list">
              {[...cal].filter((r) => r.anno >= 2027).sort((a, b) => (a.modalita === "online" ? 1 : 0) - (b.modalita === "online" ? 1 : 0) || a.anno - b.anno || a.numero - b.numero).map((row, idx) => (
                <div
                  key={row.id}
                  data-testid={`admin-promemoria-row-${idx}`}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E2D9CD] bg-[#FFFDF9] p-4"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0D9488] font-display text-sm font-semibold text-white">
                    {row.numero}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[#0C1B33]">{row.titolo}</p>
                    <p className="text-xs text-[#718096]">{row.data} · {row.ora} · {row.modalita === "online" ? "Online" : "In presenza"} · {row.anno}</p>
                  </div>
                  <button
                    data-testid={`admin-promemoria-send-${idx}`}
                    onClick={() => inviaPromemoria(row)}
                    disabled={sendingRem === row.id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#0D9488]/40 px-4 py-2 text-xs font-bold text-[#0D9488] transition-colors hover:bg-[#0D9488] hover:text-white disabled:opacity-60"
                  >
                    <BellRing className="h-3.5 w-3.5" />
                    {sendingRem === row.id ? "Invio…" : "Invia promemoria"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="admin-calendario-section">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-2xl font-semibold text-[#0C1B33]">Gestione date del calendario</h2>
              <p className="mt-1 text-sm text-[#718096]">
                Modifica le date del gruppo in presenza: i cambiamenti salvati sono subito visibili sul sito.
                Per il 2027, aggiungi righe con anno 2027.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="admin-date-add"
                onClick={addCalRow}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#0C1B33]/25 px-4 py-2 text-xs font-semibold text-[#0C1B33] transition-colors hover:border-[#0D9488] hover:text-[#0D9488]"
              >
                <Plus className="h-3.5 w-3.5" />
                Aggiungi data
              </button>
              <button
                data-testid="admin-date-save"
                onClick={saveCal}
                disabled={savingCal || cal === null}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#0D9488] px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#0C1B33] disabled:opacity-60"
              >
                <Save className="h-3.5 w-3.5" />
                {savingCal ? "Salvataggio…" : "Salva calendario"}
              </button>
            </div>
          </div>

          {cal === null ? (
            <p className="py-10 text-center text-sm text-[#718096]">Caricamento…</p>
          ) : (
            <div className="space-y-3" data-testid="admin-calendario-list">
              <div className="hidden grid-cols-12 gap-3 px-4 text-[10px] font-bold uppercase tracking-[0.14em] text-[#718096] md:grid">
                <span className="col-span-1">N.</span>
                <span className="col-span-3">Titolo incontro</span>
                <span className="col-span-3">Data</span>
                <span className="col-span-2">Ora</span>
                <span className="col-span-1">Anno</span>
                <span className="col-span-1">Modo</span>
                <span className="col-span-1"></span>
              </div>
              {cal.map((row, idx) => (
                <div
                  key={row.id}
                  data-testid={`admin-date-row-${idx}`}
                  className="grid grid-cols-2 items-center gap-3 rounded-xl border border-[#E2D9CD] bg-[#FFFDF9] p-4 md:grid-cols-12"
                >
                  <input
                    data-testid={`admin-date-numero-${idx}`}
                    type="number"
                    min="1"
                    value={row.numero}
                    onChange={(e) => updateCalRow(idx, "numero", e.target.value)}
                    className={`${calInputCls} md:col-span-1`}
                  />
                  <input
                    data-testid={`admin-date-titolo-${idx}`}
                    type="text"
                    placeholder="Titolo incontro"
                    value={row.titolo}
                    onChange={(e) => updateCalRow(idx, "titolo", e.target.value)}
                    className={`${calInputCls} md:col-span-3`}
                  />
                  <input
                    data-testid={`admin-date-data-${idx}`}
                    type="text"
                    placeholder="es. Mercoledì 3 Giugno 2026"
                    value={row.data}
                    onChange={(e) => updateCalRow(idx, "data", e.target.value)}
                    className={`${calInputCls} md:col-span-3`}
                  />
                  <input
                    data-testid={`admin-date-ora-${idx}`}
                    type="text"
                    placeholder="17:00 – 18:30"
                    value={row.ora}
                    onChange={(e) => updateCalRow(idx, "ora", e.target.value)}
                    className={`${calInputCls} md:col-span-2`}
                  />
                  <select
                    data-testid={`admin-date-anno-${idx}`}
                    value={row.anno}
                    onChange={(e) => updateCalRow(idx, "anno", Number(e.target.value))}
                    className={`${calInputCls} md:col-span-1`}
                  >
                    <option value={2026}>2026</option>
                    <option value={2027}>2027</option>
                  </select>
                  <select
                    data-testid={`admin-date-modalita-${idx}`}
                    value={row.modalita || "presenza"}
                    onChange={(e) => updateCalRow(idx, "modalita", e.target.value)}
                    className={`${calInputCls} md:col-span-1`}
                  >
                    <option value="presenza">Pres.</option>
                    <option value="online">Onl.</option>
                  </select>
                  <button
                    data-testid={`admin-date-delete-${idx}`}
                    onClick={() => removeCalRow(idx)}
                    title="Rimuovi riga"
                    className="justify-self-start rounded-full border border-[#C93B2B]/30 p-2 text-[#C93B2B] transition-colors hover:bg-[#C93B2B] hover:text-white md:col-span-1 md:justify-self-end"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {cal.length === 0 && (
                <p className="py-8 text-center text-sm text-[#718096]">
                  Nessuna data: aggiungi una riga e salva per ripristinare il calendario.
                </p>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminPage;
